// client/src/pages/BankDetails.js
// ✅ Session-cookie version of your original file:
// - uses apiFetch/apiJson (cookies included) instead of a local JWT api()
// - keeps MembershipBankManager, group payout links, my accounts, add account, copy flows
// - robust to missing user prop (won't hang on "Loading…")

import React, { useEffect, useState, useMemo } from 'react';
import logo from '../assets/chitconnect-logo-blue.png';
import { FaLink } from 'react-icons/fa';
import '../App.css';
import MembershipBankManager from '../components/MembershipBankManager';
import { apiFetch, apiJson } from '../lib/api';

export default function BankDetails({ user }) {
  // Feature flag: hide the Membership Bank Links panel entirely
  const SHOW_MEMBERSHIP_LINKS = false;

  const [accounts, setAccounts] = useState([]);

  // If opened with ?splitGroupId=ID&period=NUM, show split-payout setup (winner only).
  const params = new URLSearchParams(window.location.search);
  const splitGroupId = Number(params.get('splitGroupId') || 0);
  const splitPeriod = Number(params.get('period') || 0);
  const splitMode = !!(splitGroupId && splitPeriod);

  const [splitAccounts, setSplitAccounts] = useState({ a: '', b: '', aLabel: '', bLabel: '' });
  const [splitStatus, setSplitStatus] = useState('');
  const [splitPlan, setSplitPlan] = useState(null);

  const loadSplitPlan = async () => {
    if (!splitMode) return;
    try {
      const res = await apiFetch(`/api/payout/${splitGroupId}/${splitPeriod}/split-plan`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setSplitPlan(data);
    } catch (e) {
      // keep silent; winner might not have a plan yet
      setSplitPlan(null);
    }
  };

  const saveSplitPlan = async (e) => {
    e.preventDefault();
    setSplitStatus('');
    try {
      const a = Number(splitAccounts.a);
      const b = Number(splitAccounts.b);
      if (!a || !b || a === b) throw new Error('Please choose two different bank accounts.');
      const payload = {
        targets: [
          { bank_account_id: a, label: (splitAccounts.aLabel || '').trim() || null },
          { bank_account_id: b, label: (splitAccounts.bLabel || '').trim() || null },
        ],
      };
      const res = await apiFetch(`/api/payout/${splitGroupId}/${splitPeriod}/split-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setSplitStatus('✅ Split payout saved.');
      await loadSplitPlan();
    } catch (err) {
      setSplitStatus(`❌ ${err.message || 'Failed to save split payout.'}`);
    }
  };

  const [linkedAccountsAll, setLinkedAccountsAll] = useState([]); // across all groups (group payout links)
  const [linkedAccounts, setLinkedAccounts] = useState([]);        // for selected group (group payout links)
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ bank_name: '', account_holder: '', bsb: '', account_number: '' });

  const [expandLink, setExpandLink] = useState(true);
  const [expandMyAccounts, setExpandMyAccounts] = useState(true);
  const [expandNewAccount, setExpandNewAccount] = useState(true);

  // --- helpers ---------------------------------------------------------------
  const showMessage = (msg, duration = 4000) => {
    setMessage(msg);
    if (duration) setTimeout(() => setMessage(''), duration);
  };

  const safeJson = async (res, label = 'Request failed') => {
    const text = await res.text();
    try {
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error(data?.error || `${label} (HTTP ${res.status})`);
      return data;
    } catch {
      if (!res.ok) throw new Error(`${label} (HTTP ${res.status})`);
      throw new Error(`${label}: unexpected non-JSON response`);
    }
  };

  // Clipboard that works on http://192.168.* too
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
    } catch {
      throw new Error('Copy failed');
    }
  };

  const copyGroupLinked = async (groupBankRow) => {
    try {
      const r = await apiJson('/api/bank/copy-token', {
        method: 'POST',
        body: JSON.stringify({ group_bank_id: Number(groupBankRow.id) }),
      });
      if (!r.ok || !r.data?.token) throw new Error(r.data?.error || 'Copy not allowed');
      const plainRes = await apiFetch(`/api/bank/copy/${r.data.token}`, {
        headers: { Accept: 'text/plain' },
      });
      const txt = await plainRes.text();
      await copyToClipboard(txt);
      showMessage('Copied');
    } catch (e) {
      showMessage(e.message || 'Copy failed');
    }
  };

  const copyMyAccount = async (userBankRow) => {
    try {
      const r = await apiJson('/api/bank/copy-token', {
        method: 'POST',
        // send both keys to match either server shape
        body: JSON.stringify({
          user_bank_id: Number(userBankRow.id),
          bank_account_id: Number(userBankRow.id),
        }),
      });
      if (!r.ok || !r.data?.token) throw new Error(r.data?.error || 'Copy not allowed');
      const plainRes = await apiFetch(`/api/bank/copy/${r.data.token}`, {
        headers: { Accept: 'text/plain' },
      });
      const txt = await plainRes.text();
      await copyToClipboard(txt);
      showMessage('Copied');
    } catch (e) {
      showMessage(e.message || 'Copy failed');
    }
  };

  // 🔒 safely read groupId from URL (for membership linking component)
  const groupIdFromQuery = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('groupId');
      const n = Number(raw);
      return Number.isInteger(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }, []);

  // a safe way to get my user id even if user prop is missing
  const getMyId = async () => {
    if (user?.id) return user.id;
    try {
      const cached = JSON.parse(sessionStorage.getItem('user') || 'null');
      if (cached?.id) return cached.id;
    } catch {}
    const meRes = await apiFetch('/api/me');
    if (!meRes.ok) throw new Error('No session');
    const me = await meRes.json();
    const u = me?.user ?? me;
    sessionStorage.setItem('user', JSON.stringify(u));
    return u.id;
  };

  // --------------------------------------------------------------------------

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([fetchGroups(), fetchAccounts()]);
      } catch {
        // handled inside
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // re-run if user changes

  useEffect(() => {
    if (selectedGroup) fetchLinkedAccounts(selectedGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  useEffect(() => {
    if (groups && groups.length) fetchAllLinkedAccounts();
    else setLinkedAccountsAll([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // --- Data loaders (group payout links + my bank accounts) -----------------

  const fetchAccounts = async () => {
  try {
    // Prefer the session-scoped endpoint; fall back to legacy by id
    let res = await apiFetch('/api/bank/me');
    if (!res.ok && user?.id) res = await apiFetch(`/api/bank/user/${user.id}`);

    const payload = await safeJson(res, 'Failed to fetch accounts');

    // Normalize: array | {data:array} | {rows:array}
    const rows =
      Array.isArray(payload)        ? payload :
      Array.isArray(payload?.data)  ? payload.data :
      Array.isArray(payload?.rows)  ? payload.rows :
      [];

    setAccounts(rows);
  } catch (e) {
    setAccounts([]);
    showMessage(e.message || 'Error fetching accounts');
  }
};

  const fetchGroups = async () => {
    try {
      const resMy = await apiFetch(`/api/groups/my`);
      if (resMy.ok) {
        const dataMy = await safeJson(resMy, 'Failed to fetch groups');
        setGroups(Array.isArray(dataMy) ? dataMy : []);
        return;
      }
    } catch {}
    try {
      const resAll = await apiFetch(`/api/groups`);
      if (!resAll.ok) throw new Error();
      const dataAll = await safeJson(resAll, 'Failed to fetch groups');
      setGroups(Array.isArray(dataAll) ? dataAll : []);
    } catch (e) {
      setGroups([]);
      showMessage(e.message || 'Error fetching groups');
    }
  };

  const fetchLinkedAccounts = async (groupId) => {
    try {
      const res = await apiFetch(`/api/bank/group/${groupId}`);
      const data = await safeJson(res, 'Failed to fetch linked accounts');
      const rows = (Array.isArray(data) ? data : []).map((r, i) => ({
        ...r,
        __key: `${r.bsb}:${r.account_number}:${i}`,
      }));
      setLinkedAccounts(rows);
    } catch (e) {
      setLinkedAccounts([]);
      showMessage(e.message || 'Error fetching linked accounts');
    }
  };

  const fetchAllLinkedAccounts = async () => {
    try {
      if (!groups?.length) {
        setLinkedAccountsAll([]);
        return;
      }
      const results = await Promise.all(
        groups.map(async (g) => {
          try {
            const res = await apiFetch(`/api/bank/group/${g.id}`);
            const d = await safeJson(res, 'Failed to fetch group links');
            return (Array.isArray(d) ? d : []).map((r) => ({
              ...r,
              group_id: g.id,
            }));
          } catch {
            return [];
          }
        })
      );
      const flat = results.flat();
      const uniq = new Map();
      flat.forEach((r) => {
        const key = `${r.bsb}:${r.account_number}`;
        if (!uniq.has(key)) uniq.set(key, r);
      });
      setLinkedAccountsAll(Array.from(uniq.values()));
    } catch (e) {
      setLinkedAccountsAll([]);
      showMessage(e.message || 'Error fetching all linked accounts');
    }
  };

  // --- Actions for organiser/group payout links -----------------------------

  const handleLink = async (acc) => {
    if (!selectedGroup) return showMessage('Please select a group');
    if (!acc?.id) return showMessage('Missing bank account id');

    try {
      const res = await apiFetch(`/api/bank/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // send both snake_case and camelCase for server compatibility
        body: JSON.stringify({
          group_id: Number(selectedGroup),
          groupId: Number(selectedGroup),
          bank_account_id: Number(acc.id),
          bankAccountId: Number(acc.id),
        }),
      });
      await safeJson(res, 'Link failed');
      showMessage('Linked successfully');
      await Promise.all([fetchLinkedAccounts(selectedGroup), fetchAllLinkedAccounts()]);
      window.dispatchEvent(new Event('chit:bank-updated'));
    } catch (e) {
      showMessage(e.message || 'Error linking account');
    }
  };

  const handleUnlink = async (acc) => {
    if (!selectedGroup) return showMessage('Please select a group');
    if (!acc?.id) return showMessage('Invalid account row');

    try {
      const res = await apiFetch(`/api/bank/unlink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          group_id: Number(selectedGroup),
          groupId: Number(selectedGroup),
          group_bank_id: Number(acc.id),
          groupBankId: Number(acc.id),
        }),
      });
      await safeJson(res, 'Unlink failed');
      showMessage('Unlinked successfully');
      await Promise.all([fetchLinkedAccounts(selectedGroup), fetchAllLinkedAccounts()]);
      window.dispatchEvent(new Event('chit:bank-updated'));
    } catch (e) {
      showMessage(e.message || 'Error unlinking');
    }
  };

  // --- Manage my personal bank accounts ------------------------------------

  const handleDeleteAccount = async (acc) => {
    const isLinkedAnywhere = linkedAccountsAll.some(
      (a) =>
        String(a.account_number) === String(acc.account_number) &&
        String(a.bsb) === String(acc.bsb)
    );
    if (isLinkedAnywhere) return showMessage('Please unlink from the group(s) first');

    try {
      const myId = await getMyId();
      const res = await apiFetch(`/api/bank/user/${myId}/account/${acc.id}`, {
        method: 'DELETE',
      });
      await safeJson(res, 'Delete failed');
      await Promise.all([fetchAccounts(), fetchAllLinkedAccounts()]);
      showMessage('Deleted successfully');
      window.dispatchEvent(new Event('chit:bank-updated'));
    } catch (e) {
      showMessage(e.message || 'Error deleting');
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    const account_holder = String(form.account_holder || '').trim();
    const bsb = String(form.bsb || '').trim();
    const account_number = String(form.account_number || '').trim();

    if (!account_holder || !bsb || !account_number) return showMessage('All fields are required');
    if (!/^\d{6}$/.test(bsb)) return showMessage('BSB must be exactly 6 digits');
    if (!/^\d{6,12}$/.test(account_number)) return showMessage('Account number must be 6–12 digits');

    try {
      const myId = await getMyId();
      const res = await apiFetch(`/api/bank/user/${myId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_holder, bsb, account_number }),
      });
      await safeJson(res, 'Failed to create');
      showMessage('Bank account added');
      setForm({ bank_name: '', account_holder: '', bsb: '', account_number: '' });
      await fetchAccounts();
      window.dispatchEvent(new Event('chit:bank-updated'));
    } catch (e) {
      showMessage(e.message || 'Server error');
    }
  };

  // --- UI -------------------------------------------------------------------

  if (loading) {
    return (
      <div className="main-content">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <img src={logo} alt="ChitConnect" className="login-logo" />
      </div>

      {message && <div className="toast-message">{message}</div>}

      {/* 🔗 Membership-level linking (appears when ?groupId= is present) */}
      {SHOW_MEMBERSHIP_LINKS && groupIdFromQuery && (
        <div className="collapsible-group open">
          <div className="collapsible-header">
            <h2 className="collapsible-title">
              <FaLink style={{ marginRight: 8 }} />
              Membership Bank Links — Group #{groupIdFromQuery}
            </h2>
          </div>
          <div className="collapsible-content">
            <MembershipBankManager groupId={groupIdFromQuery} />
          </div>
        </div>
      )}

      {/* Link / Unlink Section (organiser/group payout linking) */}
      <div className={`collapsible-group ${expandLink ? 'open' : ''}`}>
        <div className="collapsible-header" onClick={() => setExpandLink(!expandLink)}>
          <h2 className="collapsible-title">
            <FaLink style={{ marginRight: '8px' }} />
            Group Payout Links
          </h2>
        </div>
        {expandLink && (
          <div className="collapsible-content">
            <select
              value={selectedGroup || ''}
              onChange={(e) => setSelectedGroup(Number(e.target.value))}
              className="styled-dropdown styled-dropdown-highlight"
              style={{ marginBottom: '1rem' }}
            >
              <option value="">Select Group</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>

            {selectedGroup && (
              <>
                <h3>🔗 Linked Bank Accounts (Group Payout)</h3>
                {linkedAccounts.length > 0 ? (
                  linkedAccounts.map((acc) => (
                    <div key={acc.__key} className="bank-card">
                      <strong>{acc.account_holder}</strong>
                      <p>{acc.bsb} / {acc.account_number}</p>
                      <button className="btn btn-ghost btn-sm" onClick={() => copyGroupLinked(acc)} title="Copy details">Copy</button>
                      <button className="btn btn-primary full-width" onClick={() => handleUnlink(acc)}>
                        Unlink
                      </button>
                    </div>
                  ))
                ) : (
                  <p>No linked accounts.</p>
                )}

                <h3 style={{ marginTop: '30px' }}>🏦 Available Bank Accounts</h3>
                {accounts
                  .filter(
                    (acc) =>
                      !linkedAccounts.some(
                        (link) =>
                          String(link.account_number) === String(acc.account_number) &&
                          String(link.bsb) === String(acc.bsb)
                      )
                  )
                  .map((acc) => (
                    <div key={acc.id} className="bank-card">
                      <strong>{acc.account_holder}</strong>
                      <p>{acc.bsb} / {acc.account_number}</p>
                      <button className="btn btn-primary full-width" onClick={() => handleLink(acc)}>
                        Link
                      </button>
                    </div>
                  ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* My Accounts */}
      <div className={`collapsible-group ${expandMyAccounts ? 'open' : ''}`}>
        <div className="collapsible-header" onClick={() => setExpandMyAccounts(!expandMyAccounts)}>
          <h2 className="collapsible-title">💼 My Bank Accounts</h2>
        </div>
        {expandMyAccounts && (
          <div className="collapsible-content">
            {accounts.length === 0 && <p>You have no bank accounts yet.</p>}
            {accounts.map((acc) => {
              const isLinkedAnywhere = linkedAccountsAll.some(
                (a) =>
                  String(a.account_number) === String(acc.account_number) &&
                  String(a.bsb) === String(acc.bsb)
              );
              return (
                <div key={acc.id} className="bank-card">
                  <strong>{acc.account_holder}</strong>
                  <p>{acc.bsb} / {acc.account_number}</p>
                  <button className="btn btn-ghost btn-sm" onClick={() => copyMyAccount(acc)} title="Copy details">Copy</button>
                  {isLinkedAnywhere && <p className="warning-text">⚠ Please unlink from group(s) to delete</p>}
                  <button
                    className="btn btn-danger full-width"
                    onClick={() => handleDeleteAccount(acc)}
                    disabled={isLinkedAnywhere}
                  >
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add New Account */}
      <div className={`collapsible-group ${expandNewAccount ? 'open' : ''}`}>
        <div className="collapsible-header" onClick={() => setExpandNewAccount(!expandNewAccount)}>
          <h2 className="collapsible-title">➕ Add a Bank Account</h2>
        </div>
        {expandNewAccount && (
          <div className="collapsible-content">
            <form onSubmit={handleCreateAccount} className="bank-form">
              <input
                type="text"
                name="account_holder"
                value={form.account_holder}
                onChange={handleChange}
                placeholder="Account Holder"
                required
              />
              <input
                type="text"
                name="bsb"
                value={form.bsb}
                onChange={handleChange}
                placeholder="BSB (6 digits)"
                required
              />
              <input
                type="text"
                name="account_number"
                value={form.account_number}
                onChange={handleChange}
                placeholder="Account Number"
                required
              />
              <button className="btn btn-primary full-width" type="submit">
                Create
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
